import React, { useState, useEffect } from 'react';
import { UserInfo } from '../types';

interface UserInfoFormProps {
  userInfo: UserInfo;
  onUpdateUserInfo: (userInfo: UserInfo) => void;
  totalAssetValue: number;
}

const UserInfoForm: React.FC<UserInfoFormProps> = ({ 
  userInfo,
  onUpdateUserInfo, 
  totalAssetValue 
}) => {

  const setCurrentAge = (age: number) => { 
    onUpdateUserInfo({
      ...userInfo,
      currentAge: age
    });
  }

  const setRetirementAge = (age: number) => { 
    onUpdateUserInfo({
      ...userInfo,
      retirementAge: age
    });
  }

  const setAnnualExpenses = (expenses: number) => { 
    onUpdateUserInfo({
      ...userInfo,
      annualExpenses: expenses
    });
  }

  // Calculate withdrawal rate based on annual expenses and total assets
  const withdrawalRate = totalAssetValue > 0 
    ? ((userInfo.annualExpenses / totalAssetValue) * 100).toFixed(2) 
    : '0.00';
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-bold mb-4">Your Information</h2>
      
      <div className="grid grid-cols-1 gap-6">
        <div className="mb-2">
          <label htmlFor="currentAge" className="block text-sm font-medium text-gray-700 mb-1">
            Current Age
          </label>
          <input
            type="number"
            id="currentAge"
            value={userInfo.currentAge}
            onChange={(e) => setCurrentAge(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="18"
            max="100"
          />
          <p className="text-xs text-gray-500 mt-1">
            Your current age is used as the starting point for all calculations.
          </p>
        </div>
        
        <div className="mb-2">
          <label htmlFor="retirementAge" className="block text-sm font-medium text-gray-700 mb-1">
            Desired Retirement Age: {userInfo.retirementAge}
          </label>
          <div className="flex items-center">
            <span className="text-xs mr-2">{Math.max(userInfo.currentAge, 20)}</span>
            <div className="relative flex-grow mx-2">
              {/* Progress bar showing time from current age to retirement */}
              <div className="absolute w-full h-2 bg-gray-200 rounded-lg">
                <div 
                  className="h-full bg-blue-300 rounded-lg" 
                  style={{
                    width: `${Math.min(100, Math.max(0, ((userInfo.retirementAge - userInfo.currentAge) / (90 - userInfo.currentAge)) * 100))}%`
                  }}
                ></div>
              </div>
              <input
                type="range"
                id="retirementAge"
                value={userInfo.retirementAge}
                onChange={(e) => setRetirementAge(parseInt(e.target.value))}
                className="w-full h-2 bg-transparent appearance-none cursor-pointer relative z-10"
                min={Math.max(userInfo.currentAge, 20)}
                max={90}
                step={1}
              />
            </div>
            <span className="text-xs ml-2">90</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Early</span>
              <span className={userInfo.retirementAge <= 55 ? "text-green-500 font-bold" : ""}>55</span>
              <span className={userInfo.retirementAge > 55 && userInfo.retirementAge <= 65 ? "text-blue-500 font-bold" : ""}>65</span>
              <span className={userInfo.retirementAge > 65 ? "text-orange-500 font-bold" : ""}>Late</span>
            </div>
            <div className="text-xs font-medium text-blue-600 text-right">
              {userInfo.retirementAge > userInfo.currentAge ? `${userInfo.retirementAge - userInfo.currentAge} years until retirement` : "Already at retirement age"}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            When you plan to stop working and start living off your investments. This determines how long your portfolio needs to grow.
          </p>
        </div>
        
        <div className="mb-2">
          <label htmlFor="annualExpenses" className="block text-sm font-medium text-gray-700 mb-1">
            Annual Expenses in Retirement ($)
          </label>
          <input
            type="number"
            id="annualExpenses"
            value={userInfo.annualExpenses}
            onChange={(e) => setAnnualExpenses(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="0"
            step="1000"
          />
          <p className="text-xs text-gray-500 mt-1">
            How much you expect to spend annually after retiring. This determines your required withdrawal rate.
          </p>
        </div>
      </div>
      
      <div className="mt-6 bg-blue-50 p-4 rounded-md">
        <h3 className="text-lg font-semibold text-blue-800 mb-2">Withdrawal Rate</h3>
        <p className="text-blue-800">
          Based on your annual expenses, your withdrawal rate would be <span className="font-bold">{withdrawalRate}%</span> of your current portfolio.
        </p>
        {parseFloat(withdrawalRate) > 4 && totalAssetValue > 0 && (
          <p className="text-red-600 mt-2">
            This withdrawal rate is higher than the traditional 4% safe withdrawal rate. Consider increasing your savings or reducing your expenses.
          </p>
        )}
      </div>
    </div>
  );
};

export default UserInfoForm;